import { useCallback } from 'react';
import { User } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import debounce from 'lodash.debounce';
import { PERMISSIONS } from '@repo/shared/domain';
import { Event } from '@repo/shared/domain';
import { Project } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { Badge } from '@repo/components';
import { RequirementChip } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import { ChipList } from '@repo/components';
import { FilterAsyncAutoComplete } from '@repo/components';
import { FilterAutoCompleteById } from '@repo/components';
import { FilterDateRange } from '@repo/components';
import { FilterSearch } from '@repo/components';
import { getPartnerCompanyEvents, getEventTypes, getZones } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		userIds: (search.userIds as string) || '',
		projectIds: (search.projectIds as string) || '',
		typeIds: (search.typeIds as string) || '',
		zoneIds: (search.zoneIds as string) || '',
		groupIds: (search.groupIds as string) || '',
		from: (search.from as string) || '',
		until: (search.until as string) || '',
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'startTime:desc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Events');
	const { search, userIds, projectIds, typeIds, zoneIds, from, until, page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();

	const {
		data: eventsResponse,
		isLoading: eventsLoading,
		error: _getPartnerCompanyEventsError,
	} = useQuery({
		queryKey: [
			'partner-company-events',
			partnerId,
			{ search, userIds, page, sort, typeIds, projectIds, zoneIds, from, until },
		],
		queryFn: () =>
			getPartnerCompanyEvents(
				partnerId,
				constructQuery({ search, userIds, page: page, sort, typeIds, projectIds, zoneIds, from, until }),
			),
		enabled: !userLoading && !!authenticatedUser,
	});
	const events = eventsResponse?.data || [];

	const {
		data: eventTypesResponse,
		isLoading: eventTypesLoading,
		error: _getEventTypesError,
	} = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
		enabled: !userLoading && !!authenticatedUser,
	});
	const eventTypes = eventTypesResponse?.data || [];

	const {
		data: zonesResponse,
		isLoading: zonesLoading,
		error: _getZonesError,
	} = useQuery({
		queryKey: ['zones'],
		queryFn: () => getZones(),
		enabled: !userLoading && !!authenticatedUser,
	});
	const zones = zonesResponse?.data || [];

	const updateQueryParams = useCallback(
		(params: Record<string, string | undefined>) => {
			navigate({
				search: (prev) => ({ ...prev, ...params }),
			});
			return null;
		},
		[navigate],
	);

	if (!verifyPermission(PERMISSIONS.partnerCompanyEventsView)) {
		navigate({ to: `/partner/${partnerId}` });
		return null;
	}

	// Loading = gating initial load
	if (userLoading || eventTypesLoading || zonesLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	const handleSortChange = (sortModel: any[]) => {
		if (sortModel.length > 0) {
			const { field, sort: sortDir } = sortModel[0];
			updateQueryParams({ sort: `${field}:${sortDir}` });
		}
	};

	const handlePageChange = (newPage: number) => {
		updateQueryParams({ page: newPage });
	};

	const handleRowClick = ({ row }) => {
		navigate({ to: `/partner/${partnerId}/events/${row.id}` });
		return null;
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: {
		groupIds?: string[] | string;
		zoneIds?: string[] | string;
		typeIds?: string[] | string;
		userIds?: string[] | string;
		projectIds?: string[] | string;
		search?: string;
	}) => {
		updateQueryParams(filterData);
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const columns: DataGridColumn<Event>[] = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
		},
		{
			field: 'project',
			headerName: 'Project',
			flex: 1,
			renderCell: (c: { value: Project | null }) => {
				return c.value?.name ? <Badge>{c.value.name}</Badge> : null;
			},
			sortable: false,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			renderCell: (c: any) => <RequirementChip object={c.row} />,
			sortable: false,
		},
		{
			field: 'startTime',
			headerName: 'Date From',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'finishTime',
			headerName: 'Date To',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'zones',
			headerName: 'Zones',
			flex: 1,
			renderCell: (c) => <ChipList data={c.value} getLabel={(o) => o?.name} />,
			sortable: false,
		},
		{
			field: 'groups',
			headerName: 'Companies',
			flex: 1,
			renderCell: (c) => <ChipList data={c.value} getLabel={(o) => o?.name} showAll />,
			sortable: false,
		},
		{
			field: 'users',
			headerName: 'Users',
			flex: 1,
			renderCell: (c) => (
				<ChipList
					data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
					getLabel={(o) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
		{
			field: 'admins',
			headerName: 'Contacts',
			flex: 1,
			renderCell: (c) => (
				<ChipList
					data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
					getLabel={(o) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
	];

	return (
		<div className="p-6">
			<div className="mt-2 flex flex-row flex-wrap gap-2">
				<div key="search" className="w-3/5 flex-grow flex gap-2">
					<FilterSearch
						key="search"
						label="Search Event"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
					/>
					<FilterAsyncAutoComplete
						key="userFilter"
						label="Filter Users"
						value={userIds || []}
						onChange={(userIds) => handleFilterChange({ userIds })}
						dataSource="/api/data-sources/users/data"
						optionLabel="name"
						name="userIds"
						multiple
					/>
				</div>
				<div key="filters" className="w-2/5 flex-grow flex gap-2">
					<FilterAsyncAutoComplete
						key="projectFilter"
						label="Filter Projects"
						dataSource="/api/data-sources/projects/data"
						optionLabel="name"
						filter="groupId"
						values={{ groupId: partnerId }}
						value={projectIds || []}
						onChange={(projectIds) => handleFilterChange({ projectIds })}
						multiple
					/>
					<FilterAutoCompleteById
						key="typeFilter"
						label="Filter Types"
						options={eventTypes}
						value={typeIds || []}
						onChange={(typeIds) => handleFilterChange({ typeIds })}
						multiple
					/>
					<FilterAutoCompleteById
						key="zoneFilter"
						label="Filter Zones"
						options={zones}
						value={zoneIds || []}
						onChange={(zoneIds) => handleFilterChange({ zoneIds })}
						multiple
					/>
					<FilterDateRange
						label="Filter Date Range"
						value={
							from || until
								? {
										from,
										until,
									}
								: undefined
						}
						onChange={handleDateFilterChange}
						sx={{ width: 330 }}
					/>
				</div>
			</div>
			{/* Data Grid */}
			<div className="mt-2  border border-gray-300 rounded bg-white shadow-sm">
				<DataGrid
					data={events}
					columns={columns}
					isLoading={eventsLoading}
					rowCount={eventsResponse?.count || 0}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={({ page }) => handlePageChange(page)}
					onSortModelChange={handleSortChange}
					sortingOrder={['asc', 'desc']}
					pageSize={eventsResponse?.pageSize}
					page={eventsResponse?.page}
					onRowClick={handleRowClick}
					pagination
					getRowClassName={(params) => (params.row.markedForReview ? 'error' : '')}
					onReload={() => queryClient.invalidateQueries({ queryKey: ['partner-company-events', partnerId] })}
				/>
			</div>
		</div>
	);
}
